/**
 * 3D viewer for a rectangular reinforced concrete beam.
 *
 * Geometry:
 *   - Concrete: BoxGeometry of (L_span × h × b), extruded along world x.
 *   - Longitudinal bars: cylinders along the +x axis, at top and bottom.
 *   - Stirrups: closed rectangular loops in the y-z plane, distributed along x
 *     using the spacing zones returned by the backend.
 *
 * Coordinates in scene:
 *   x : along the beam axis (length L)
 *   y : vertical (depth h)
 *   z : perpendicular (width b)
 */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds, Html } from '@react-three/drei';
import * as THREE from 'three';
import type { BeamType, PointLoad, ShearZone } from './types';

interface Props {
  L: number;        // span in cm
  b: number;        // width in cm
  h: number;        // depth in cm
  cover: number;    // cm
  d_stirrup: number; // cm
  d_bar_top: number; // cm
  d_bar_bot: number; // cm
  n_bars_top: number;
  n_bars_bot: number;
  /** Compression-steel rows (drawn one row inboard from the tension row). */
  n_bars_top_comp?: number;
  n_bars_bot_comp?: number;
  zones: ShearZone[];
  /** When true the beam visually hangs (floor above): the band indicator and
   *  support hints flip to the top instead of the bottom. */
  inverted?: boolean;
  /** Beam type — drives the contextual elements drawn around the beam (slab,
   *  walls of masonry, foundation, etc.) to match the standard typological
   *  diagrams. */
  beamType?: BeamType;
  /** Point loads (drawn as 3D arrows pointing downward on top of the beam). */
  appliedLoads?: PointLoad[];
}

const COLOR_CONCRETE = '#94A3B8';
const COLOR_CONCRETE_EDGE = '#94A3B8';
const COLOR_BAR_TOP = '#F59E0B';
const COLOR_BAR_BOT = '#F59E0B';
const COLOR_BAR_COMP = '#8B5CF6';   // compression bars (doubly-reinforced)
const COLOR_STIRRUP = '#0891B2';

export function Beam3DView({
  L,
  b,
  h,
  cover,
  d_stirrup,
  d_bar_top,
  d_bar_bot,
  n_bars_top,
  n_bars_bot,
  n_bars_top_comp = 0,
  n_bars_bot_comp = 0,
  zones,
  inverted = false,
  beamType = 'rectangular',
  appliedLoads = [],
}: Props) {
  // Scale cm → scene units. We compute the scale separately for the long axis
  // (L) and the section dims (h, b) and pick the one that keeps the geometry
  // bounded to sensible scene extents:
  //    L max → 5 units    (along world x)
  //    h max → 2.5 units  (along world y — the depth)
  //    b max → 2.5 units  (along world z — the width)
  // The MIN scale wins so nothing exceeds its budget. This gives true beam
  // proportions: a deep beam looks tall, a flat (chata) looks wide, a long
  // beam looks long. Camera distance is constant so all types frame cleanly.
  const s = Math.min(5 / L, 2.5 / Math.max(h, 1), 2.5 / Math.max(b, 1));

  // Stirrup vertical positions across the span (one per zone, evenly spaced)
  const stirrupXs = useMemo(() => {
    const xs: number[] = [];
    let cursor = 0;
    for (const z of zones) {
      let x = cursor + z.spacing / 2;
      while (x <= cursor + z.length && xs.length < 200) {
        xs.push(x);
        x += z.spacing;
      }
      cursor += z.length;
    }
    return xs;
  }, [zones]);

  // Stirrup inset (corners of the stirrup pass through the corner bars, like in columns)
  const corner_off = cover + d_stirrup + Math.max(d_bar_top, d_bar_bot) / 2;
  const stirY_top = (h / 2 - corner_off) * s;
  const stirY_bot = (-h / 2 + corner_off) * s;
  const stirZ_pos = (b / 2 - corner_off) * s;
  const stirZ_neg = (-b / 2 + corner_off) * s;

  // Shared BufferGeometry for the stirrup loop (closed rectangle in the y-z plane at x=0).
  const stirrupGeom = useMemo(() => {
    const pts = [
      new THREE.Vector3(0, stirY_top, stirZ_neg),
      new THREE.Vector3(0, stirY_top, stirZ_pos),
      new THREE.Vector3(0, stirY_bot, stirZ_pos),
      new THREE.Vector3(0, stirY_bot, stirZ_neg),
      new THREE.Vector3(0, stirY_top, stirZ_neg),  // close
    ];
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, [stirY_top, stirY_bot, stirZ_pos, stirZ_neg]);

  // Bar positions along z at top and bottom faces
  const barTopY = stirY_top;
  const barBotY = stirY_bot;
  // Compression bars sit one row inboard (4 cm offset, matches the 2D drawing).
  const barTopCompY = (h / 2 - corner_off - 4) * s;
  const barBotCompY = (-h / 2 + corner_off + 4) * s;
  const barRadiusTop = (d_bar_top / 2) * s;
  const barRadiusBot = (d_bar_bot / 2) * s;
  const barTopsZ = useMemo(() => barPositionsZ(n_bars_top, b, corner_off, s), [n_bars_top, b, corner_off, s]);
  const barBotsZ = useMemo(() => barPositionsZ(n_bars_bot, b, corner_off, s), [n_bars_bot, b, corner_off, s]);
  const barTopsCompZ = useMemo(() => barPositionsZ(n_bars_top_comp, b, corner_off, s), [n_bars_top_comp, b, corner_off, s]);
  const barBotsCompZ = useMemo(() => barPositionsZ(n_bars_bot_comp, b, corner_off, s), [n_bars_bot_comp, b, corner_off, s]);

  const halfL = (L / 2) * s;

  return (
    <Canvas
      // Camera placed at ~1.5x the longest scene dimension (5 units) so the
      // full beam fits in the view with margin to spare. FOV 38 keeps the
      // perspective natural but not exaggerated.
      camera={{ position: [6.5, 4.5, 6.5], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      // preserveDrawingBuffer lets toDataURL() return the last rendered frame
      // (needed for capturing the 3D as a PNG snapshot for the PDF export).
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.55} />
      <directionalLight position={[5, 5, 5]} intensity={0.9} />
      <directionalLight position={[-5, -2, -3]} intensity={0.25} />

      <Grid
        args={[20, 20]}
        cellSize={0.25}
        cellThickness={0.4}
        cellColor="#475569"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#334155"
        fadeDistance={14}
        fadeStrength={1.2}
        // The grid sits at floor level for a normal beam, and at the ceiling
        // for an inverted one (so it looks like the beam hangs from a slab).
        position={[0, inverted ? +h * s * 0.5 + 0.01 : -h * s * 0.5 - 0.01, 0]}
        infiniteGrid
      />

      {/* Bounds auto-fits the camera to the beam geometry on first render,
          guaranteeing the whole element is visible regardless of the
          beam type's proportions. `observe` re-fits when the geometry
          changes. `margin` adds breathing room around the bounding box. */}
      <Bounds fit clip observe margin={1.4}>
      {/* Contextual elements per type (slab / walls / foundation) — placed
          BEFORE the beam so the transparent concrete renders on top. */}
      <BeamContext beamType={beamType} L={L} b={b} h={h} s={s} inverted={inverted} />
      {/* CONCRETE BEAM */}
      <mesh>
        <boxGeometry args={[L * s, h * s, b * s]} />
        <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.32} roughness={0.85} metalness={0.05} />
      </mesh>
      {/* Edges of the box for crisp silhouette */}
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(L * s, h * s, b * s)]} />
        <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
      </lineSegments>

      {/* LONGITUDINAL BARS — top */}
      {barTopsZ.map((z, i) => (
        <Cylinder
          key={`top-${i}`}
          radius={barRadiusTop}
          length={L * s}
          position={[0, barTopY, z]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_TOP}
        />
      ))}
      {/* LONGITUDINAL BARS — bottom */}
      {barBotsZ.map((z, i) => (
        <Cylinder
          key={`bot-${i}`}
          radius={barRadiusBot}
          length={L * s}
          position={[0, barBotY, z]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_BOT}
        />
      ))}
      {/* LONGITUDINAL BARS — compression top (doubly-reinforced positive moment) */}
      {barTopsCompZ.map((z, i) => (
        <Cylinder
          key={`top-c-${i}`}
          radius={barRadiusTop * 0.9}
          length={L * s}
          position={[0, barTopCompY, z]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_COMP}
        />
      ))}
      {/* LONGITUDINAL BARS — compression bottom (doubly-reinforced negative moment) */}
      {barBotsCompZ.map((z, i) => (
        <Cylinder
          key={`bot-c-${i}`}
          radius={barRadiusBot * 0.9}
          length={L * s}
          position={[0, barBotCompY, z]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_COMP}
        />
      ))}

      {/* STIRRUPS — rectangular loops in the y-z plane at each x position */}
      {stirrupXs.map((xCm, i) => {
        const x = (xCm - L / 2) * s;
        return (
          <line key={`stir-${i}`} position={[x, 0, 0]}>
            <primitive attach="geometry" object={stirrupGeom} />
            <lineBasicMaterial color={COLOR_STIRRUP} />
          </line>
        );
      })}

      {/* ZONE BAND INDICATORS — strips of color marking confinement vs central
          zones. Placed on the FLOOR for a normal beam (just below the section)
          or on the CEILING for an inverted beam (the beam hangs from above). */}
      {(() => {
        const bandY = inverted ? +h * s * 0.5 + 0.03 : -h * s * 0.5 - 0.03;
        const bandRotX = inverted ? +Math.PI / 2 : -Math.PI / 2;
        let cursorCm = 0;
        return zones.map((z, i) => {
          const x1 = (cursorCm - L / 2) * s;
          const x2 = (cursorCm + z.length - L / 2) * s;
          cursorCm += z.length;
          const isConf = z.label.toLowerCase().includes('confin');
          const color = isConf ? '#F59E0B' : '#8B5CF6';
          const mid = (x1 + x2) / 2;
          const w = x2 - x1;
          return (
            <mesh key={`band-${i}`} position={[mid, bandY, 0]} rotation={[bandRotX, 0, 0]}>
              <planeGeometry args={[w, b * s * 1.05]} />
              <meshBasicMaterial color={color} transparent opacity={0.16} />
            </mesh>
          );
        });
      })()}
      {/* Reference tick for span end */}
      <mesh
        position={[halfL + 0.05, (inverted ? +h * s * 0.5 + 0.03 : -h * s * 0.5 - 0.03), 0]}
        rotation={[inverted ? +Math.PI / 2 : -Math.PI / 2, 0, 0]}
      >
        <planeGeometry args={[0.02, b * s * 1.05]} />
        <meshBasicMaterial color="#94A3B8" />
      </mesh>

      {/* APPLIED POINT LOADS — 3D arrows pointing downward at each load
          position. Each arrow is a vertical cylinder (shaft) + a cone
          (head) + an HTML label with the magnitude. */}
      {appliedLoads.map((load) => {
        const beamTopY = (h * s) / 2;
        const arrowTotalH = Math.max(beamTopY * 1.2, 0.4);
        const headH = arrowTotalH * 0.35;
        const shaftH = arrowTotalH - headH;
        const headRadius = Math.min(beamTopY * 0.35, 0.10);
        const shaftRadius = headRadius * 0.30;
        // Arrow positioned along x at the load's position, centered laterally (z=0)
        const xWorld = (load.x_cm - L / 2) * s;
        // Tip just above the beam top
        const tipY = beamTopY + 0.04;
        const headCenterY = tipY + headH / 2;
        const shaftCenterY = tipY + headH + shaftH / 2;
        const labelY = tipY + arrowTotalH + 0.05;
        const P_tonf = load.P_kgf / 1000;
        return (
          <group key={`load3d-${load.id}`}>
            {/* Shaft */}
            <mesh position={[xWorld, shaftCenterY, 0]}>
              <cylinderGeometry args={[shaftRadius, shaftRadius, shaftH, 14]} />
              <meshStandardMaterial color="#F59E0B" metalness={0.3} roughness={0.45} />
            </mesh>
            {/* Cone arrowhead pointing DOWN (apex at the bottom). Default cone
                in three.js has its apex at +y; rotate 180° around X so apex is
                at -y (pointing down toward the beam). */}
            <mesh position={[xWorld, headCenterY, 0]} rotation={[Math.PI, 0, 0]}>
              <coneGeometry args={[headRadius, headH, 18]} />
              <meshStandardMaterial color="#F59E0B" metalness={0.4} roughness={0.40} />
            </mesh>
            {/* Magnitude label as HTML overlay */}
            <Html
              position={[xWorld, labelY, 0]}
              center
              distanceFactor={8}
              style={{
                pointerEvents: 'none',
                fontFamily: 'monospace',
                fontSize: 12,
                fontWeight: 700,
                color: '#F59E0B',
                background: 'rgba(15,23,42,0.92)',
                border: '1px solid #F59E0B',
                borderRadius: 4,
                padding: '2px 6px',
                whiteSpace: 'nowrap',
              }}
            >
              {P_tonf >= 1 ? `${P_tonf.toFixed(2)} tf` : `${load.P_kgf.toFixed(0)} kgf`}
            </Html>
          </group>
        );
      })}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

/* =========================================================================
 *  CONTEXT — slab / walls / foundation depending on beam type
 * ========================================================================= */

const COLOR_SLAB = '#64748B';        // gray concrete slab
const COLOR_WALL = '#B45309';        // masonry / brick wall
const COLOR_WALL_LIGHT = '#92400E';  // darker brick for accent
const COLOR_FOOTING = '#475569';     // foundation footing concrete

function BeamContext({
  beamType,
  L,
  b,
  h,
  s,
  inverted,
}: {
  beamType: BeamType;
  L: number;
  b: number;
  h: number;
  s: number;
  inverted: boolean;
}) {
  // Dimensions in scene units
  const beamL = L * s;
  const beamH = h * s;
  const beamB = b * s;

  // The slab is a thin horizontal plate (20 cm depth) extending beyond the
  // beam on either side. The "slab" represents either a real concrete slab or
  // a band of masonry on either side of the beam, depending on type.
  const slabDepth = 20 * s;              // 20 cm = typical slab depth
  const slabSide = beamB * 4;            // extend 4× beam width on each side
  const slabTotalZ = beamB + 2 * slabSide;

  switch (beamType) {
    case 'rectangular': {
      // NORMAL: slab on top of the beam (T-shape in cross-section).
      // The beam hangs below the slab.
      const slabY = beamH / 2 + slabDepth / 2;
      return (
        <group>
          <mesh position={[0, slabY, 0]}>
            <boxGeometry args={[beamL, slabDepth, slabTotalZ]} />
            <meshStandardMaterial color={COLOR_SLAB} transparent opacity={0.65} roughness={0.85} />
          </mesh>
        </group>
      );
    }
    case 'invertida': {
      // INVERTIDA: slab at the BOTTOM, beam sticks UP (⊥-shape).
      const slabY = -beamH / 2 - slabDepth / 2;
      return (
        <group>
          <mesh position={[0, slabY, 0]}>
            <boxGeometry args={[beamL, slabDepth, slabTotalZ]} />
            <meshStandardMaterial color={COLOR_SLAB} transparent opacity={0.65} roughness={0.85} />
          </mesh>
        </group>
      );
    }
    case 'chata': {
      // CHATA: slab at SAME level as the beam (beam is flush with slab top).
      // Slab thickness = beam depth h. Slab extends laterally on each side.
      return (
        <group>
          <mesh position={[0, 0, +(beamB / 2 + slabSide / 2)]}>
            <boxGeometry args={[beamL, beamH, slabSide]} />
            <meshStandardMaterial color={COLOR_SLAB} transparent opacity={0.78} roughness={0.85} />
          </mesh>
          <mesh position={[0, 0, -(beamB / 2 + slabSide / 2)]}>
            <boxGeometry args={[beamL, beamH, slabSide]} />
            <meshStandardMaterial color={COLOR_SLAB} transparent opacity={0.78} roughness={0.85} />
          </mesh>
        </group>
      );
    }
    case 'peraltada': {
      // PERALTADA: by default we show a "colgante" version — slab on top,
      // beam hangs deeply below. The user can rotate the camera to see how
      // tall the beam is below the slab.
      const slabY = inverted ? -beamH / 2 - slabDepth / 2 : beamH / 2 + slabDepth / 2;
      return (
        <group>
          <mesh position={[0, slabY, 0]}>
            <boxGeometry args={[beamL, slabDepth, slabTotalZ]} />
            <meshStandardMaterial color={COLOR_SLAB} transparent opacity={0.65} roughness={0.85} />
          </mesh>
        </group>
      );
    }
    case 'solera': {
      // SOLERA: small beam on TOP of a masonry wall. Wall below the beam.
      const wallH = beamH * 4;                // wall taller than beam
      const wallY = -beamH / 2 - wallH / 2;
      return (
        <group>
          {/* Masonry wall under the beam */}
          <mesh position={[0, wallY, 0]}>
            <boxGeometry args={[beamL, wallH, beamB]} />
            <meshStandardMaterial color={COLOR_WALL} transparent opacity={0.78} roughness={1.0} />
          </mesh>
          {/* Subtle brick-band texture: thin horizontal stripes */}
          {[0.2, 0.4, 0.6, 0.8].map((f, i) => (
            <mesh
              key={`brick-${i}`}
              position={[0, wallY + wallH / 2 - wallH * f, 0]}
            >
              <boxGeometry args={[beamL * 1.001, 0.005, beamB * 1.005]} />
              <meshBasicMaterial color={COLOR_WALL_LIGHT} />
            </mesh>
          ))}
        </group>
      );
    }
    case 'confinamiento': {
      // CONFINAMIENTO: small beam embedded BETWEEN masonry walls (above and
      // below). The beam stitches two stacks of brick together.
      const wallH = beamH * 3;
      return (
        <group>
          {/* wall BELOW */}
          <mesh position={[0, -beamH / 2 - wallH / 2, 0]}>
            <boxGeometry args={[beamL, wallH, beamB]} />
            <meshStandardMaterial color={COLOR_WALL} transparent opacity={0.78} roughness={1.0} />
          </mesh>
          {/* wall ABOVE */}
          <mesh position={[0, +beamH / 2 + wallH / 2, 0]}>
            <boxGeometry args={[beamL, wallH, beamB]} />
            <meshStandardMaterial color={COLOR_WALL} transparent opacity={0.78} roughness={1.0} />
          </mesh>
          {/* brick stripes (just a couple per wall) */}
          {[-1.5, -0.5, 0.5, 1.5].map((f, i) => (
            <mesh
              key={`brick-${i}`}
              position={[0, f * (wallH / 3) + (f > 0 ? +beamH / 2 + wallH / 2 : -beamH / 2 - wallH / 2), 0]}
            >
              <boxGeometry args={[beamL * 1.001, 0.005, beamB * 1.005]} />
              <meshBasicMaterial color={COLOR_WALL_LIGHT} />
            </mesh>
          ))}
        </group>
      );
    }
    case 'arriostre': {
      // ARRIOSTRE: at foundation level, between two footings/columns.
      const footW = beamB * 3;
      const footH = beamH * 1.5;
      const footingY = -beamH / 2 - footH / 2;
      const xOffset = beamL / 2;
      return (
        <group>
          {/* Left footing */}
          <mesh position={[-xOffset, footingY, 0]}>
            <boxGeometry args={[footW, footH, footW]} />
            <meshStandardMaterial color={COLOR_FOOTING} transparent opacity={0.45} roughness={0.9} />
          </mesh>
          {/* Right footing */}
          <mesh position={[+xOffset, footingY, 0]}>
            <boxGeometry args={[footW, footH, footW]} />
            <meshStandardMaterial color={COLOR_FOOTING} transparent opacity={0.45} roughness={0.9} />
          </mesh>
          {/* Soil hint (very faint horizontal plate) */}
          <mesh position={[0, footingY, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[beamL * 1.8, footW * 1.6]} />
            <meshBasicMaterial color="#3F2D14" transparent opacity={0.18} />
          </mesh>
        </group>
      );
    }
    case 'borde': {
      // BORDE: edge beam at the perimeter — slab only on ONE side (asymmetric).
      return (
        <group>
          <mesh position={[0, +beamH / 4, +(beamB / 2 + slabSide / 2)]}>
            <boxGeometry args={[beamL, beamH * 0.4, slabSide]} />
            <meshStandardMaterial color={COLOR_SLAB} transparent opacity={0.65} roughness={0.85} />
          </mesh>
        </group>
      );
    }
    default:
      return null;
  }
}

/* Helpers */

function barPositionsZ(n: number, b: number, off_corner: number, s: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [0];
  const zL = (-b / 2 + off_corner) * s;
  const zR = (b / 2 - off_corner) * s;
  return Array.from({ length: n }, (_, i) => zL + (i * (zR - zL)) / (n - 1));
}

function Cylinder({
  radius,
  length,
  position,
  rotation,
  color,
}: {
  radius: number;
  length: number;
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
}) {
  return (
    <mesh position={position} rotation={rotation}>
      <cylinderGeometry args={[radius, radius, length, 14]} />
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
